/**
 * THE FIVE PALETTES A HOUSE MAY WEAR.
 *
 * Not a colour picker, and the reason is the guest menu. It is type on linen at
 * a 17px floor, read on a phone, outdoors, at dusk, by somebody who has had a
 * glass of wine. A free hex lets a house choose a pale ochre accent, make its
 * own prices invisible, and never find out why nobody ordered the dessert.
 *
 * Each of these is drawn from something these houses are actually built from,
 * and each was chosen so that the accent carries white text and the secondary
 * carries the same body ink at the same size. Retuning one here retunes it for
 * every house that wears it, which is the other reason the database stores a
 * NAME and never a colour.
 *
 * `accent` replaces clay — buttons, the current tab, the price emphasis.
 * `secondary` replaces olive — kickers, availability windows, quiet affirmation.
 */

export type PaletteName = "lut" | "vie" | "nistru" | "paie" | "piatra";

export type Palette = {
  name: PaletteName;
  /** What the owner sees in the picker. Materials, not colour names. */
  label: string;
  /** One line placing it, so a choice can be made without squinting at swatches. */
  hint: string;
  accent: string;
  accentDeep: string;
  accentPale: string;
  secondary: string;
  secondaryDeep: string;
  secondaryPale: string;
};

export const PALETTES: Record<PaletteName, Palette> = {
  lut: {
    name: "lut",
    label: "Lut",
    hint: "Ceramică arsă și frunză de nuc. Paleta cu care a fost desenat produsul.",
    accent: "#b99d75",
    accentDeep: "#8f7550",
    accentPale: "#f3ece1",
    secondary: "#53624e",
    secondaryDeep: "#3d4a39",
    secondaryPale: "#e8ede6",
  },
  vie: {
    name: "vie",
    label: "Vie",
    hint: "Must tânăr și frunză de viță. Pentru o casă care trăiește din vin.",
    accent: "#8c3f52",
    accentDeep: "#6b2c3d",
    accentPale: "#f5e8ea",
    secondary: "#5a6b4a",
    secondaryDeep: "#414f35",
    secondaryPale: "#e9eee5",
  },
  nistru: {
    name: "nistru",
    label: "Nistru",
    hint: "Apă de dimineață și salcie. Rece, potolită, bună lângă râu.",
    accent: "#3f6b7a",
    accentDeep: "#2c4e5a",
    accentPale: "#e6eef1",
    secondary: "#6b7264",
    secondaryDeep: "#4e5449",
    secondaryPale: "#ecedea",
  },
  paie: {
    name: "paie",
    label: "Paie",
    hint: "Grâu copt și mușchi. Cald și luminos, pentru curte și grădină.",
    accent: "#a8792c",
    accentDeep: "#805a1c",
    accentPale: "#f6efdf",
    secondary: "#5f6b3f",
    secondaryDeep: "#464f2d",
    secondaryPale: "#eceee3",
  },
  piatra: {
    name: "piatra",
    label: "Piatră",
    hint: "Cotileț și fum. Sobru, aproape fără culoare — lasă mâncarea în față.",
    accent: "#8a5a48",
    accentDeep: "#6a4234",
    accentPale: "#f1e9e5",
    secondary: "#67655f",
    secondaryDeep: "#4b4945",
    secondaryPale: "#ececeb",
  },
};

export const PALETTE_NAMES = Object.keys(PALETTES) as PaletteName[];

export function paletteOf(name: string | null | undefined): Palette {
  return PALETTES[(name ?? "lut") as PaletteName] ?? PALETTES.lut;
}
